// components/ResourceSearchResults.js
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../auth';
import ContentCard from './ContentCard';

export default function ResourceSearchResults({ filters }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    const abortController = new AbortController();

    const fetchResults = async () => {
      // Don't fetch if all filters are 'all'
      if (filters.type === 'all' && filters.grade === 'all' && filters.subject === 'all') {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        if (filters.type && filters.type !== 'all') {
          params.append('type', filters.type);
        }
        if (filters.grade && filters.grade !== 'all') {
          params.append('grades', filters.grade);
        }
        if (filters.subject && filters.subject !== 'all') {
          params.append('subject', filters.subject);
        }

        const res = await fetchWithAuth(`/api/resources/filter/?${params.toString()}`, {
          signal: abortController.signal,
        });

        if (!res.ok) {
          throw new Error('Failed to load resources');
        }

        const data = await res.json();
        if (!ignore) {
          setResults(data);
        }
      } catch (err) {
        if (err.name !== 'AbortError' && !ignore) {
          setError('שגיאה בטעינת המשאבים');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchResults();

    return () => {
      ignore = true;
      abortController.abort();
    };
  }, [filters.type, filters.grade, filters.subject]);

  const navigate = useNavigate();

  const handleCardClick = (item) => {
    if (item.url) {
      navigate(item.url);
    }
  };

  if (filters.type === 'all' && filters.grade === 'all' && filters.subject === 'all') {
    return null;
  }

  if (loading) {
    return (
      <div className="mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-gray-100 dark:border-secondary-700 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-secondary-700 rounded w-1/3 mb-3"></div>
              <div className="h-6 bg-gray-200 dark:bg-secondary-700 rounded w-2/3 mb-2"></div>
              <div className="h-4 bg-gray-200 dark:bg-secondary-700 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 dark:bg-secondary-700 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8 text-center py-8">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="mb-8 text-center py-20 bg-white dark:bg-secondary-800 rounded-xl border border-dashed border-gray-300 dark:border-secondary-600">
        <p className="text-xl text-gray-500 dark:text-gray-400">לא נמצאו משאבים התואמים את הסינון שלך.</p>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">
          תוצאות חיפוש
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400 mr-2">
            ({results.length} תוצאות)
          </span>
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {results.map((item) => (
          <ContentCard
            key={`${item.model_type}-${item.id}`}
            item={item}
            onClick={() => handleCardClick(item)}
          />
        ))}
      </div>
    </div>
  );
}

